import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  PlayArrow as ProcessIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material'
import { ticketsAPI, agentsAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'

const AdminTicketManagementPage = () => {
  const [ticketNumber, setTicketNumber] = useState('')
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editAssignedTo, setEditAssignedTo] = useState('')
  const [editData, setEditData] = useState('')
  const [editDataError, setEditDataError] = useState(null)

  const handleSearch = async () => {
    if (!ticketNumber || ticketNumber.trim() === '') {
      setError('Please enter a ticket number')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)
    setTicket(null)

    try {
      const data = await ticketsAPI.getTicket(ticketNumber.trim())
      setTicket(data)
    } catch (err) {
      console.error('Failed to fetch ticket:', err)
      setError(`Failed to fetch ticket: ${err.response?.data?.detail || err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    if (!ticket) return

    setLoading(true)
    setError(null)

    try {
      const data = await ticketsAPI.getTicket(ticket.id)
      setTicket(data)
      setSuccess('Ticket refreshed successfully')
    } catch (err) {
      console.error('Failed to refresh ticket:', err)
      setError(`Failed to refresh ticket: ${err.response?.data?.detail || err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleForceProcess = async () => {
    if (!ticket) return

    setProcessing(true)
    setError(null)
    setSuccess(null)

    try {
      await agentsAPI.processTicket(ticket.id)
      setSuccess('Ticket processing initiated successfully')
      // Refresh ticket to get updated status
      setTimeout(() => handleRefresh(), 1000)
    } catch (err) {
      console.error('Failed to process ticket:', err)
      setError(`Failed to process ticket: ${err.response?.data?.detail || err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  const openEditDialog = () => {
    if (!ticket) return

    setEditAssignedTo(ticket.assigned_to || '')
    setEditData(JSON.stringify(ticket.data || {}, null, 2))
    setEditDataError(null)
    setEditDialogOpen(true)
  }

  const handleEditSave = async () => {
    // Validate JSON
    try {
      const parsedData = JSON.parse(editData)

      setLoading(true)
      setError(null)

      // Update the ticket via API
      const updates = {
        assigned_to: editAssignedTo,
        data: parsedData
      }

      await ticketsAPI.updateTicket(ticket.id, updates)

      setEditDialogOpen(false)
      setSuccess('Ticket updated successfully')

      // Refresh ticket to show new data
      setTimeout(() => handleRefresh(), 500)

    } catch (err) {
      if (err.message === 'Invalid JSON format') {
        setEditDataError('Invalid JSON format')
      } else {
        setError(`Failed to update ticket: ${err.response?.data?.detail || err.message}`)
      }
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleString()
    } catch {
      return dateString
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'info'
      case 'waiting':
        return 'warning'
      case 'processing':
        return 'primary'
      case 'complete':
        return 'success'
      case 'error':
        return 'error'
      default:
        return 'default'
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'system':
        return 'default'
      case 'user':
        return 'primary'
      case 'agent':
        return 'secondary'
      case 'external':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getTimeInStatus = (modifiedAt) => {
    if (!modifiedAt) return 'N/A'
    try {
      const modified = new Date(modifiedAt)
      const now = new Date()
      const diffMs = now - modified
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffDays > 0) {
        return `${diffDays}d ${diffHours % 24}h`
      } else if (diffHours > 0) {
        return `${diffHours}h ${diffMins % 60}m`
      } else {
        return `${diffMins}m`
      }
    } catch {
      return 'N/A'
    }
  }

  return (
    <AdminPageWrapper title="Ticket Management">
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Ticket
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label="Ticket Number"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
            size="small"
            sx={{ flexGrow: 1, maxWidth: 400 }}
            placeholder="Enter ticket ID or number"
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && !ticket && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {ticket && (
        <Paper sx={{ p: 3 }}>
          {/* Header with Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Ticket #{ticket.id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={openEditDialog}
                disabled={loading}
                size="small"
                color="primary"
              >
                Edit
              </Button>
              <Button
                variant="contained"
                startIcon={<ProcessIcon />}
                onClick={handleForceProcess}
                disabled={processing || loading}
                size="small"
                color="warning"
              >
                {processing ? 'Processing...' : 'Force Process'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Status Information */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Status:
                </Typography>
                <Chip
                  label={ticket.status}
                  color={getStatusColor(ticket.status)}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Role:
                </Typography>
                <Chip
                  label={ticket.role}
                  color={getRoleColor(ticket.role)}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  <TimeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  Time in Status:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {getTimeInStatus(ticket.modified_at)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Type:
                </Typography>
                <Typography variant="body1">
                  {ticket.type}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Assigned To:
                </Typography>
                <Typography variant="body1">
                  {ticket.assigned_to}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Created By:
                </Typography>
                <Typography variant="body1">
                  {ticket.created_by}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Timestamps */}
          <Typography variant="h6" gutterBottom>
            Timestamps
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                  <TableCell>{formatDate(ticket.created_at)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Modified At</TableCell>
                  <TableCell>{formatDate(ticket.modified_at)}</TableCell>
                </TableRow>
                {ticket.archived && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Archived At</TableCell>
                    <TableCell>{formatDate(ticket.archived_at)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Ticket Data */}
          <Typography variant="h6" gutterBottom>
            Ticket Data
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
            <pre style={{ margin: 0, fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(ticket.data || {}, null, 2)}
            </pre>
          </Paper>

          {/* Conversation Thread Count */}
          {ticket.conversation_thread && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Conversation Messages: {ticket.conversation_thread.length}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Ticket #{ticket?.id}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Update ticket header information. Changes will be saved immediately.
          </Alert>

          <TextField
            label="Assigned To"
            value={editAssignedTo}
            onChange={(e) => setEditAssignedTo(e.target.value)}
            fullWidth
            margin="normal"
            size="small"
            helperText="Email address of the user this ticket is assigned to"
          />

          <TextField
            label="Ticket Data (JSON)"
            value={editData}
            onChange={(e) => {
              const value = e.target.value
              setEditData(value)
              // Try to parse JSON to show immediate validation feedback
              try {
                JSON.parse(value)
                setEditDataError(null)
              } catch (err) {
                setEditDataError('Invalid JSON format: ' + err.message)
              }
            }}
            fullWidth
            margin="normal"
            multiline
            rows={15}
            error={!!editDataError}
            helperText={editDataError || "Edit the ticket data as JSON. Must be valid JSON format."}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={loading || !!editDataError}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminTicketManagementPage
